import { useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useResetPin } from "../../hooks/queries/useAuthQueries";
import { toastService } from "../../providers";
import { extractAxiosErrorMessage } from "../../services/utils/errorUtils";
import { isDeadTokenMessage } from "../../components/auth/config/authActionCodes";
import { pinSetupValidators } from "../../lib/validations/authValidations";
import CustomUnauthorized from "../../components/ui/CustomUnauthorized/CustomUnauthorized";
import AuthLayout from "../../components/auth/authLayout/AuthLayout";
import AuthCard from "../../components/auth/authCard/AuthCard";
import PinSetupFields from "../../components/auth/PinSetupFields/PinSetupFields";
import CustomButton from "../../components/ui/CustomButton/CustomButton";

function ResetPin() {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get("token");
  const navigate = useNavigate();
  const [isTokenDead, setIsTokenDead] = useState(false);

  const { mutate: resetPin, isPending } = useResetPin();

  const { control, handleSubmit, reset, trigger, watch } = useForm({
    defaultValues: { newPin: "", confirmPin: "" },
    mode: "onTouched",
  });

  const newPin = watch("newPin");
  const confirmPin = watch("confirmPin");
  const hasFormErrors =
    Boolean(pinSetupValidators.newPin(newPin)) ||
    Boolean(pinSetupValidators.confirmPin(newPin, confirmPin));

  const onSubmit = (values) => {
    resetPin(
      {
        token: resetToken,
        newPin: values.newPin,
        confirmPin: values.confirmPin,
      },
      {
        onSuccess: (data) => {
          toastService.success(
            data.remarks || "PIN reset successfully. Proceed to login.",
          );
          navigate("/auth/login");
        },
        onError: (error) => {
          const message = extractAxiosErrorMessage(
            error,
            "Could not reset your PIN. Please try again.",
          );
          if (isDeadTokenMessage(message)) {
            setIsTokenDead(true);
          } else {
            reset({ newPin: "", confirmPin: "" });
          }
          toastService.error(message);
        },
      },
    );
  };

  if (!resetToken) {
    return (
      <CustomUnauthorized
        leftTitle="Access Denied!"
        leftMessage="Please ensure you clicked the full link from your email."
        actions={[
          {
            label: "Back To Home",
            href: "/",
          },
        ]}
      />
    );
  }

  return (
    <AuthLayout>
      <AuthCard
        title="Reset PIN"
        subtitle={
          <div className="text-black-50 text-xs sm:text-sm">
            {isTokenDead
              ? "This reset link is no longer valid."
              : "Set up a new PIN to access your account"}
          </div>
        }
      >
        <form
          className="flex flex-col gap-2.5 sm:gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          {!isTokenDead && (
            <>
              <PinSetupFields
                control={control}
                disabled={isPending}
                trigger={trigger}
              />
              <CustomButton
                type="submit"
                radius="xl"
                size="sm"
                fullWidth
                disabled={isPending || hasFormErrors}
                loading={isPending}
              >
                Reset PIN
              </CustomButton>
            </>
          )}

          {isTokenDead && (
            <CustomButton
              type="button"
              radius="xl"
              size="sm"
              fullWidth
              onClick={() => navigate("/auth/forgot-pin")}
            >
              Request New Reset Link
            </CustomButton>
          )}
        </form>
      </AuthCard>
    </AuthLayout>
  );
}

export default ResetPin;
